import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * ARA-SEÇ — uzun listeler için açılır liste yerine "yazdıkça gelen" seçim kutusu.
 * Kullanıcı yüzlerce cari ya da ürün arasında gezmez; iki harf yazar, eşleşen
 * en çok 12 kayıt gelir. ↑ ↓ ile gezilir, Enter seçer, Esc kapatır.
 * Arama Türkçe büyük/küçük harfe ve İ/I farkına duyarsızdır.
 */
export type AraSecenek = { id: string | number; ad: string; alt?: string | null };

const duz = (s: string) => s.toLocaleLowerCase('tr').replace(/ı/g, 'i').replace(/İ/g, 'i')
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

export default function AraSec({
  id, secenekler, deger, degis, yerTutucu = 'Yazmaya başlayın…', bosEtiket, disabled, required, yukleniyor, azami = 12, ...kalan
}: {
  id?: string; secenekler: AraSecenek[]; deger: string; degis: (id: string) => void;
  yerTutucu?: string;
  /** Verilirse listenin başında "seçimi kaldır" satırı olur (örn. "— kayıtsız cari —"). */
  bosEtiket?: string;
  disabled?: boolean; required?: boolean; yukleniyor?: boolean; azami?: number;
  'data-tur'?: string;
}) {
  const secili = useMemo(() => secenekler.find((s) => String(s.id) === deger) ?? null, [secenekler, deger]);
  const [metin, setMetin] = useState('');
  const [acik, setAcik] = useState(false);
  const [imlec, setImlec] = useState(0);
  const kutu = useRef<HTMLDivElement>(null);

  // Dışarıdan seçim değişince kutuda adı göster.
  useEffect(() => { if (!acik) setMetin(secili?.ad ?? ''); }, [secili, acik]);

  const q = duz(metin.trim());
  const yaziyor = acik && metin !== (secili?.ad ?? '');
  const eslesen = useMemo(() => {
    if (!yaziyor || !q) return secenekler.slice(0, azami);
    const kelimeler = q.split(/\s+/);
    const bas: AraSecenek[] = [], ic: AraSecenek[] = [];
    for (const s of secenekler) {
      const h = duz(`${s.ad} ${s.alt ?? ''}`);
      if (!kelimeler.every((k) => h.includes(k))) continue;
      (h.startsWith(q) ? bas : ic).push(s);
      if (bas.length >= azami) break;
    }
    return [...bas, ...ic].slice(0, azami);
  }, [secenekler, q, yaziyor, azami]);
  const toplam = useMemo(() => {
    if (!yaziyor || !q) return secenekler.length;
    const kelimeler = q.split(/\s+/);
    return secenekler.reduce((n, s) => n + (kelimeler.every((k) => duz(`${s.ad} ${s.alt ?? ''}`).includes(k)) ? 1 : 0), 0);
  }, [secenekler, q, yaziyor]);

  function sec(s: AraSecenek | null) {
    degis(s ? String(s.id) : '');
    setMetin(s?.ad ?? ''); setAcik(false);
  }

  return (
    <div ref={kutu} style={{ position: 'relative' }} className="ccd-arasec" data-klavye-ust
         onBlur={(e) => { if (!kutu.current?.contains(e.relatedTarget as Node)) { setAcik(false); setMetin(secili?.ad ?? ''); } }}>
      <input id={id} className="ccd-input" role="combobox" aria-expanded={acik} aria-autocomplete="list" autoComplete="off" autoCorrect="off" spellCheck={false} enterKeyHint="search"
             disabled={disabled} required={required && !deger} placeholder={yukleniyor ? 'Yükleniyor…' : yerTutucu} value={metin} data-tur={kalan['data-tur']}
             onFocus={(e) => { setAcik(true); setImlec(0); e.target.select(); }}
             onChange={(e) => { setMetin(e.target.value); setAcik(true); setImlec(0); }}
             onKeyDown={(e) => {
               if (e.key === 'ArrowDown') { e.preventDefault(); setAcik(true); setImlec((n) => Math.min(eslesen.length - 1, n + 1)); }
               else if (e.key === 'ArrowUp') { e.preventDefault(); setImlec((n) => Math.max(0, n - 1)); }
               else if (e.key === 'Enter' && acik) { e.preventDefault(); if (eslesen[imlec]) sec(eslesen[imlec]); }
               else if (e.key === 'Escape') { setAcik(false); setMetin(secili?.ad ?? ''); }
             }} />
      {deger && !disabled && (
        <button type="button" tabIndex={-1} aria-label="Seçimi kaldır" title="Seçimi kaldır" onClick={() => sec(null)} className="ccd-arasec-sil"
                style={{ position: 'absolute', right: 8, top: 0, height: '100%', border: 0, background: 'none', cursor: 'pointer', color: 'var(--ccd-text-secondary, #777)', fontSize: 16 }}>×</button>
      )}
      {acik && !disabled && (
        <div className="ccd-oneri" role="listbox">
          {bosEtiket && !q && (
            <button type="button" className="ccd-oneri-satir" onMouseDown={(e) => { e.preventDefault(); sec(null); }}>
              <span className="ccd-oneri-ad ccd-muted">{bosEtiket}</span></button>
          )}
          {eslesen.length === 0 ? (
            <div className="ccd-oneri-bos">{yukleniyor ? <><span className="ccd-oneri-donen" /> Yükleniyor…</> : `“${metin.trim()}” ile eşleşen kayıt yok`}</div>
          ) : eslesen.map((s, n) => (
            <button key={s.id} type="button" role="option" aria-selected={n === imlec}
                    className={`ccd-oneri-satir${n === imlec ? ' ccd-is-active' : ''}`}
                    onMouseEnter={() => setImlec(n)} onMouseDown={(e) => { e.preventDefault(); sec(s); }}>
              <span className="ccd-oneri-ad">{s.ad}</span>
              {s.alt && <span className="ccd-muted">{s.alt}</span>}
            </button>
          ))}
          {toplam > eslesen.length && (
            <div className="ccd-oneri-bos" style={{ fontSize: 12.5 }}>
              {yaziyor && q ? `${toplam} eşleşmeden ilk ${eslesen.length} tanesi — daraltmak için yazmaya devam edin` : `${toplam} kayıt var — bulmak için yazın`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
